import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  CheckCircle,
  XCircle,
  Search,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  owner: 'Agency Owner',
  specialist: 'Specialist',
  staff: 'Staff',
};

/**
 * My Staff Section
 * 
 * Manage agency team members, roles, and invitations.
 */
const MyStaffSection = ({ agency, agencyId, isReadOnly }) => {
  const { token } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get(
          `${API}/owner-console/agencies/${agencyId}/users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Filter to staff roles only
        const staffMembers = (response.data.users || response.data || [])
          .filter(u => ['owner', 'specialist', 'staff'].includes(u.role));
        setStaff(staffMembers);
      } catch (err) {
        console.error('Failed to fetch staff:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (token && agencyId) {
      fetchStaff();
    }
  }, [token, agencyId]);
  
  // Filter staff by search
  const filteredStaff = staff.filter(member => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.name?.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query) ||
      member.role?.toLowerCase().includes(query)
    );
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="my-staff-section">
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">My Staff</h2>
              <p className="text-sm text-slate-500">
                Manage team members and permissions
              </p>
            </div>
          </div>
          
          {!isReadOnly && (
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus size={16} className="mr-2" />
              Invite Staff
            </Button>
          )}
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total Staff</p>
          <p className="text-2xl font-bold text-slate-900">{staff.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Active</p>
          <p className="text-2xl font-bold text-emerald-600">
            {staff.filter(s => s.is_active).length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Pending Invites</p>
          <p className="text-2xl font-bold text-amber-600">0</p>
        </div>
      </div>
      
      {/* Search & Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search staff members..."
            className="pl-10 bg-white"
          />
        </div>
      </div>
      
      {/* Staff List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Member
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Role
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Status
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Last Active
              </th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-slate-500">
                  {searchQuery ? 'No staff members match your search' : 'No staff members found'}
                </td>
              </tr>
            ) : (
              filteredStaff.map((member) => (
                <tr key={member.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-sm font-medium text-slate-600">
                          {(member.name || member.email || '?')[0].toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{member.name || 'Unnamed'}</p>
                        <p className="text-xs text-slate-500">{member.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="bg-slate-50">
                      <Shield size={12} className="mr-1" />
                      {ROLE_LABELS[member.role] || member.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {member.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle size={12} className="mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">
                        <XCircle size={12} className="mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {member.last_login_at 
                      ? new Date(member.last_login_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" disabled={isReadOnly}>
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem>View Profile</DropdownMenuItem>
                        <DropdownMenuItem>Edit Permissions</DropdownMenuItem>
                        <DropdownMenuItem>Resend Invite</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600">Remove</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyStaffSection;
